import React, { useState, useEffect } from "react";
import {
  Button,
  IconButton,
  Modal,
  ModalOverlay,
  SimpleGrid,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Box,
  HStack,
  Icon,
  Spinner,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  FaEdit,
  FaPlus,
  FaTrash,
  FaMapMarkerAlt,
  FaMapSigns,
  FaBus,
  FaClock,
  FaDollarSign,
} from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const RoutEdit = ({ route_id, RefreshData }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [busRoute, setBusRoute] = useState(null);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [routeName, setRouteName] = useState("");
  const [routeStartName, setRouteStartName] = useState("");

  const [selectedBus, setSelectedBus] = useState("");
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const schoolId = sessionStorage.getItem("school_id");
  const userId = sessionStorage.getItem("user_id");

  // Fetch bus details
  const fetchBusDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/admin/bus/vehicle/list_all_bus_details.php`,
        {
          params: { school_id: schoolId },
        }
      );
      if (response.data.success) {
        setBuses(response.data.data);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch bus details.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch bus details.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch bus route details
  const fetchBusRoute = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/admin/bus/bus_route/view_bus_route.php`,
        {
          params: { id: route_id },
        }
      );
      console.log(response);

      if (response.data.success) {
        const data = response.data.data;
        setBusRoute(data);
        setRouteName(data.end_place);
        setRouteStartName(data.start_place);
        setSelectedBus(data.bus_id || "");

        const formattedPickupPoints = data.stops.map((stop) => ({
          point: stop.stop_name,
          stop_id: stop.stop_id,
          pickup_time: stop.pickup_time,
          drop_time: stop.drop_time,
          fee_amount: stop.fee_amount,
        }));

        setPickupPoints(formattedPickupPoints);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch bus route details.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchBusRoute();
    fetchBusDetails();
  }, [route_id]);

  const handleAddPickupPoint = () => {
    setPickupPoints([...pickupPoints, { point: "", stop_id: null }]);
  };

  const handleRemovePickupPoint = async (index, stop_id) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/admin/bus/bus_route/delete_stop_name.php`,
        {
          data: {
            user_id: userId,
            school_id: schoolId,
            id: stop_id, // stop_id of the point to delete
          },
        }
      );

      if (response.data.success) {
        setPickupPoints(pickupPoints.filter((_, i) => i !== index));
        toast({
          title: "Success",
          description: "Pickup point deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description:
            response.data.message || "Failed to delete pickup point.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete pickup point.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleChangePickupPoint = (index, field, value) => {
    const updatedPoints = pickupPoints.map((point, i) =>
      i === index ? { ...point, [field]: value } : point
    );
    setPickupPoints(updatedPoints);
  };

  const handleSaveChanges = async () => {
    setIsLoading(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/admin/bus/bus_route/edit_bus_route.php`,
        {
          user_id: userId,
          school_id: schoolId,
          route_id,
          end_place: routeName,
          start_place: routeStartName,
          bus_id: selectedBus,
          bus_stops: pickupPoints.map((point) => ({
            stop_id: point.stop_id,
            stop_name: point.point,
            pickup_time: point.pickup_time,
            drop_time: point.drop_time,
            fee_amount: point.fee_amount,
          })),
        }
      );

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Bus route updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose();
        RefreshData();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to update bus route.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update bus route.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <IconButton
        icon={<FaEdit />}
        colorScheme="blue"
        size="sm"
        onClick={onOpen}
        aria-label="Edit"
      />
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaEdit} boxSize={5} mr={2} />
            Edit Route Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              <FormControl mb={4}>
                <FormLabel>
                  {" "}
                  <Icon as={FaMapMarkerAlt} mr={1} color="teal.500" />
                  Start Name
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter route name"
                  value={routeStartName}
                  onChange={(e) => setRouteStartName(e.target.value)}
                />
              </FormControl>
              <FormControl mb={4}>
                <FormLabel>
                  {" "}
                  <Icon as={FaMapSigns} mr={1} color="teal.500" />
                  End Name
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter route name"
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                />
              </FormControl>
            </SimpleGrid>

            <FormControl mb={4}>
              <FormLabel>
                {" "}
                <Icon as={FaBus} mr={1} color="teal.500" />
                Select Bus
              </FormLabel>
              {loading ? (
                <Spinner size="sm" />
              ) : (
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Select a bus"
                  value={selectedBus}
                  onChange={(e) => setSelectedBus(e.target.value)}
                >
                  {buses.map((bus) => (
                    <option key={bus.id} value={bus.id}>
                      {bus.bus_no}
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>

            <Box>
              <FormLabel>
                {" "}
                <Icon as={FaMapMarkerAlt} mr={1} color="teal.500" />
                Pickup Points
              </FormLabel>
              {pickupPoints.map((pickup, index) => (
                <HStack key={index} mb={4} spacing={4}>
                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    placeholder="Pickup Point"
                    value={pickup.point}
                    onChange={(e) =>
                      handleChangePickupPoint(index, "point", e.target.value)
                    }
                  />

                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    type="time"
                    value={pickup.pickup_time}
                    onChange={(e) =>
                      handleChangePickupPoint(
                        index,
                        "pickup_time",
                        e.target.value
                      )
                    }
                  />

                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    type="time"
                    value={pickup.drop_time}
                    onChange={(e) =>
                      handleChangePickupPoint(
                        index,
                        "drop_time",
                        e.target.value
                      )
                    }
                  />

                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    type="number"
                    placeholder="Fees"
                    value={pickup.fee_amount}
                    onChange={(e) =>
                      handleChangePickupPoint(
                        index,
                        "fee_amount",
                        e.target.value
                      )
                    }
                  />

                  <IconButton
                    icon={<FaTrash />}
                    colorScheme="red"
                    size="xs"
                    onClick={() =>
                      handleRemovePickupPoint(index, pickup.stop_id)
                    }
                    aria-label="Remove Pickup Point"
                    ml="auto"
                  />
                </HStack>
              ))}
              <Button
                size="sm"
                colorScheme="teal"
                leftIcon={<FaPlus />}
                onClick={handleAddPickupPoint}
              >
                Add Pickup Point
              </Button>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              isLoading={isLoading}
              onClick={handleSaveChanges}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RoutEdit;
